/* eslint-disable no-underscore-dangle */
import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';
import { CartContext } from '@repo/contexts/CartContext';
import { LocaleContext } from '@repo/contexts/LocaleContext';
import { Puff } from 'react-loader-spinner';
import { DealerSearchContext } from '@repo/contexts/DealerSearchContext';
import { FETCH_PRODUCT_UPDATE_QUERY } from '@repo/contexts/cartQueries';
import { pushToDataLayer } from '@repo/utils';
import SanityImage from 'gatsby-plugin-sanity-image';
import { LocalizedLink } from '@repo/gatsby-localization-helpers';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import BlockContent from '../../BlockContent';
import breakpoints from '../../../breakpoints';
import sharedStyles from '../../../styles';
import Button from '../../../button';

const COD_ITEM_METAFIELD = 'company_owned_dealer_variant';
const SUBSCRIPTION_FREQUENCY_METAFIELD = 'subscription_frequency';

const Container = styled.div`
  display: block;
  width: 100%;
  margin-bottom: 1rem;

  @media (min-width: ${breakpoints.md}) {
    height: fit-content;
  }

  @media (min-width: ${breakpoints.lg}) {
    height: fit-content;
  }
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  gap: ${sharedStyles.spacing['spacing-1xl']};

  @media (min-width: ${breakpoints.md}) {
    flex-direction: row;
  }
`;

const ProductCard = styled.div`
  display: ${({ hideProductCard }) => (!hideProductCard ? 'flex' : 'none')};
  flex-direction: column;
  align-items: flex-start;
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: 0px 4px 6px -4px rgba(0, 0, 0, 0.1),
    0px 10px 15px -3px rgba(0, 0, 0, 0.1);
  border-radius: ${sharedStyles.spacing['spacing-2xs']};
  flex: 1 1 0px;

  @media (min-width: ${breakpoints.md}) {
    max-width: 381px;
  }
`;

const DealerSelectNotice = styled.div`
  margin-bottom: ${sharedStyles.spacing['spacing-1xl']};
  h3 {
    margin-bottom: ${sharedStyles.spacing['spacing-md']};
  }
`;

const InnerProductCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${sharedStyles.spacing['spacing-md']};
  background: ${({ theme }) => theme.colors.background};
  border-radius: 0 0 ${sharedStyles.spacing['spacing-2xs']}
    ${sharedStyles.spacing['spacing-2xs']};
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 1;
`;

const TopHeaderCaption = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  align-self: stretch;
  background: ${({ theme }) => theme.colors.border};
  padding: ${sharedStyles.spacing['spacing-2xs']}
    ${sharedStyles.spacing['spacing-sm']};

  span {
    text-align: center;
    width: 100%;
  }
`;

const ProductAsset = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  height: 78px;

  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;

  img {
    width: 68px;
    height: 68px;
  }
`;

const ProductDetailsSection = styled.div`
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
`;

const ProductHeading = styled.h2`
  font-weight: 700;
  line-height: 28px;
  text-align: center;
  width: 100%;
  color: ${({ theme }) => theme.colors.headline};
`;

const ProductPrice = styled.p`
  font-weight: 700;
  font-size: ${sharedStyles.fontSize.lg};
  margin: 0 0 0.25em;
  width: 100%;
  line-height: 28px;
  text-align: center;
  color: ${({ theme }) => theme.colors.headline};
`;

const HighlightSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${sharedStyles.spacing['spacing-2xs']};
  gap: ${sharedStyles.spacing['spacing-sm']};
  margin: 0 0 0.25em;
  background: ${({ theme }) => theme.colors.border};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${sharedStyles.spacing['spacing-2xs']};

  .fullWidth {
    width: 100%;
    margin: 0;
  }

  p {
    font-weight: ${sharedStyles.fontWeight.bold};
    font-size: ${sharedStyles.fontSize.base};
    line-height: 18px;
    text-align: center;
    color: ${({ theme }) => theme.colors.headline};
  }
`;

const HighlightSectionContainer = styled.h3`
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
  align-items: center;
  padding: 0px;
  gap: ${sharedStyles.spacing['spacing-sm']};

  span {
    font-size: ${sharedStyles.fontSize.lg};
    line-height: 22px;
    margin: 0;
    text-align: center;
  }
`;

const BenefitsSection = styled.div`
  font-weight: ${sharedStyles.fontWeight.regular};
  font-size: ${sharedStyles.fontSize.base};
  line-height: 24px;
  color: ${({ theme }) => theme.colors.defaultFont};
  flex: none;
  order: 1;
  align-self: stretch;
`;

const PromoHeadline = styled.h2`
  text-align: center;
  font-weight: ${sharedStyles.fontWeight.bold};
  font-size: ${sharedStyles.fontSize.base};
  line-height: 18px;
`;

const LinksSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  gap: ${sharedStyles.spacing['spacing-lg']};
  order: 2;
  align-self: stretch;
`;

const PreSalePrice = styled.span`
  text-decoration: line-through;
  font-size: ${sharedStyles.fontSize['1xl']};
`;

const SalePrice = styled.span`
  color: ${({ theme }) => theme.colors.onSaleRed};
`;

const SpinnerContainer = styled.div`
  padding-top: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

const getLocalizedValue = originalValue => {
  if (!originalValue) return originalValue;

  const value = JSON.parse(JSON.stringify(originalValue));

  value?.products?.forEach(item => {
    item?.product?.store?.variants?.forEach(variant => {
      if (variant)
        // eslint-disable-next-line no-param-reassign
        variant.store =
          variant._type === 'globalProductVariant' ? variant : variant.store;
    });
  });
  return value;
};

function BlockBuilderProductsComparisonGrid({
  value: originalValue,
  getInternalLink,
}) {
  const { tt } = useUiComponentsInternationalContext();

  const value = getLocalizedValue(originalValue);

  const dealerSearchContext = useContext(DealerSearchContext);

  const localeContext = useContext(LocaleContext);
  const currency = localeContext?.currency || 'USD';
  const { shopifyClient, formatPrice } = useContext(CartContext);
  const [isLoading, setIsLoading] = useState(true);
  const theme = useTheme();

  const storedDealer = dealerSearchContext?.storedDealer ?? null;

  const hasDealerSpecificProducts = value?.products?.some(p => {
    let dealerSpecificProduct = false;
    p?.product?.store?.variants?.forEach(variant => {
      variant?.store?.metafields?.forEach(metafield => {
        if (metafield.name === COD_ITEM_METAFIELD) {
          dealerSpecificProduct = true;
        }
      });
    });
    return dealerSpecificProduct;
  });

  const getDisplayPrice = product => {
    const variantPrice =
      currency === 'CAD'
        ? product?.store?.variants?.[0]?.canadaStore?.price
        : product?.store?.variants?.[0]?.store?.price;
    const compareAtPrice =
      currency === 'CAD'
        ? product?.store?.variants?.[0]?.canadaStore?.compareAtPrice
        : product?.store?.variants?.[0]?.store?.compareAtPrice;

    // Sale Price
    if (variantPrice && compareAtPrice) {
      return (
        <>
          <PreSalePrice>{formatPrice(compareAtPrice)}</PreSalePrice>
          <br />
          <SalePrice>SALE {formatPrice(variantPrice)}</SalePrice>
        </>
      );
    }

    // First Variant Price
    return <span>{formatPrice(variantPrice)}</span>;
  };

  useEffect(() => {
    if (
      !hasDealerSpecificProducts ||
      storedDealer ||
      !dealerSearchContext?.dealerGateModalActive
    ) {
      setIsLoading(false);
    }
  }, [hasDealerSpecificProducts, storedDealer]);

  const productArray = value?.products?.filter(Boolean) || [];

  return (
    <Container isEmpty={productArray.length === 0}>
      {isLoading && (
        <SpinnerContainer>
          <Puff
            color={theme.colors.brandPrimary}
            height={60}
            width={60}
            timeout={300000}
          />
        </SpinnerContainer>
      )}
      {!isLoading && !storedDealer && (
        <DealerSelectNotice>
          <h3>
            {tt(
              s =>
                s.blockBuilderProductsComparisonGridComponent
                  .selectDealerForInformationHeader
            )}
          </h3>
          <Button
            onClick={() => {
              dealerSearchContext?.setDealerGateModalActive(true);
            }}
          >
            {tt(
              s =>
                s.blockBuilderProductsComparisonGridComponent
                  .searchDealersButton
            )}
          </Button>
        </DealerSelectNotice>
      )}
      <InnerContainer>
        {productArray.map(productCard => {
          let hideProductCard = false;
          let showProductUnavailable = false;

          // We have dealerSearchContext
          if (dealerSearchContext) {
            const codMetafield =
              productCard?.productVariant?.store?.metafields?.find(
                metafield => metafield.name === COD_ITEM_METAFIELD
              );

            const subscriptionMetafield =
              productCard?.productVariant?.store?.metafields?.find(
                metafield => metafield.name === SUBSCRIPTION_FREQUENCY_METAFIELD
              );

            showProductUnavailable =
              currency === 'CAD' && subscriptionMetafield?.value;

            // Product has cod metafield
            if (codMetafield) {
              // We have stored dealer
              if (storedDealer) {
                if (storedDealer?.isCOS && codMetafield.value === 'false') {
                  hideProductCard = true;
                }

                if (!storedDealer?.isCOS && codMetafield.value === 'true') {
                  hideProductCard = true;
                }
              } else {
                // Hide cards until we have storedDealer
                hideProductCard = true;
              }
            }
          }
          return (
            <ProductCard
              key={productCard?._key}
              hideProductCard={hideProductCard}
            >
              {productCard?.topHeader && (
                <TopHeaderCaption>
                  <span>{productCard.topHeader}</span>
                </TopHeaderCaption>
              )}

              <InnerProductCard>
                <ProductDetailsSection>
                  {productCard?.useProductImage &&
                    productCard?.product?.store?.previewImageUrl && (
                      <ProductAsset>
                        <img
                          src={productCard.product.store.previewImageUrl}
                          alt={productCard.product.store?.title || ''}
                          width={68}
                          height={68}
                        />
                      </ProductAsset>
                    )}
                  {!productCard?.useProductImage &&
                    productCard?.media?.asset && (
                      <ProductAsset>
                        <SanityImage
                          asset={productCard.media.asset}
                          hotspot={productCard.media?.hotspot}
                          crop={productCard.media?.crop}
                          alt={
                            productCard.media?.alt ||
                            productCard?.product.store?.title ||
                            ''
                          }
                          width={68}
                          height={68}
                        />
                      </ProductAsset>
                    )}

                  {productCard?.product?.store && (
                    <>
                      <ProductHeading>
                        {productCard?.overwriteProductCardTitle
                          ? productCard?.productCardTitle
                          : productCard.product.store?.title}
                      </ProductHeading>
                      <ProductPrice>
                        {getDisplayPrice(productCard.product)}
                      </ProductPrice>
                    </>
                  )}

                  {productCard?.enableHighlightSection && (
                    <HighlightSection>
                      <HighlightSectionContainer>
                        {productCard?.leftHighlightSection && (
                          <span>{productCard.leftHighlightSection}</span>
                        )}

                        {productCard?.leftHighlightSection &&
                          productCard?.rightHighlightSection && <span>+</span>}

                        {productCard?.rightHighlightSection && (
                          <span>{productCard.rightHighlightSection}</span>
                        )}
                      </HighlightSectionContainer>

                      {productCard?.bottomHighlightSection && (
                        <p className="fullWidth">
                          {productCard.bottomHighlightSection}
                        </p>
                      )}
                    </HighlightSection>
                  )}

                  {!productCard?.enableHighlightSection &&
                    productCard?.promoSection && (
                      <PromoHeadline>{productCard.promoSection}</PromoHeadline>
                    )}
                </ProductDetailsSection>

                <BenefitsSection>
                  {productCard?.benefitsSection?.text?.length && (
                    <BlockContent
                      blocks={productCard?.benefitsSection?.text}
                      getInternalLink={getInternalLink}
                    />
                  )}
                </BenefitsSection>

                <LinksSection>
                  {productCard?.addToCart && (
                    <CartContext.Consumer>
                      {({ addToCart }) => (
                        <Button
                          title={
                            showProductUnavailable
                              ? tt(
                                  s =>
                                    s
                                      .blockBuilderProductsComparisonGridComponent
                                      .unavailableButton
                                )
                              : tt(
                                  s =>
                                    s
                                      .blockBuilderProductsComparisonGridComponent
                                      .addToCartButton
                                )
                          }
                          ariaLabel={
                            showProductUnavailable
                              ? tt(
                                  s =>
                                    s
                                      .blockBuilderProductsComparisonGridComponent
                                      .unavailableAriaLabel
                                )
                              : tt(
                                  s =>
                                    s
                                      .blockBuilderProductsComparisonGridComponent
                                      .addToCartAriaLabel
                                )
                          }
                          disabled={showProductUnavailable}
                          onClick={async () => {
                            const productId =
                              currency === 'CAD'
                                ? productCard?.productVariant?.canadaStore
                                    ?.productGid
                                : productCard.product.store.gid;

                            const variantId =
                              currency === 'CAD'
                                ? productCard?.productVariant?.canadaStore?.gid
                                : productCard?.productVariant?.store?.gid;

                            const { product } = await shopifyClient.request(
                              FETCH_PRODUCT_UPDATE_QUERY,
                              {
                                id: productId,
                              }
                            );

                            await addToCart({
                              quantity: 1,
                              sellingPlanGroups:
                                product?.sellingPlanGroups?.nodes || [],
                              selectedSellingPlan: {
                                id: product?.sellingPlanGroups?.nodes?.[0]
                                  ?.sellingPlans?.nodes?.[0]?.id,
                              },
                              variantId,
                              variantMetafields:
                                productCard?.productVariant?.store?.metafields,
                            });

                            const lineItemPrice =
                              (currency === 'CAD'
                                ? productCard?.productVariant?.canadaStore
                                    ?.price
                                : productCard?.productVariant?.store?.price) ||
                              0;

                            pushToDataLayer('add_to_cart', {
                              ecommerce: {
                                currency,
                                value: Number(lineItemPrice).toFixed(2),
                              },
                              items: [
                                {
                                  variant: {
                                    sku: productCard?.productVariant?.store
                                      ?.sku,
                                    title:
                                      productCard?.productVariant?.store?.title,
                                    price: {
                                      amount: Number(lineItemPrice)
                                        .toFixed(2)
                                        .toString(),
                                    },
                                  },
                                  product: {
                                    title: productCard?.product?.store?.title,
                                    productType:
                                      productCard?.product?.store?.productType,
                                    vendor: productCard?.product?.store?.vendor,
                                  },
                                  quantity: 1,
                                },
                              ],
                            });
                          }}
                        >
                          {showProductUnavailable
                            ? tt(
                                s =>
                                  s.blockBuilderProductsComparisonGridComponent
                                    .callYourLocalDealerButton
                              )
                            : tt(
                                s =>
                                  s.blockBuilderProductsComparisonGridComponent
                                    .addToCartButton
                              )}
                        </Button>
                      )}
                    </CartContext.Consumer>
                  )}

                  {!showProductUnavailable && productCard?.cta?.caption && (
                    <>
                      {productCard?.cta?.linkType === 'internal' && (
                        <LocalizedLink
                          to={getInternalLink({
                            ...productCard.cta,
                            reference: productCard.cta?.internalLink,
                            currency,
                          })}
                        >
                          {productCard.cta.caption}
                        </LocalizedLink>
                      )}
                      {productCard?.cta?.linkType === 'external' && (
                        <a
                          href={productCard?.cta?.href}
                          target={productCard?.cta?.blank ? '_blank' : '_self'}
                          rel="noreferrer"
                        >
                          {productCard.cta.caption}
                        </a>
                      )}
                    </>
                  )}
                </LinksSection>
              </InnerProductCard>
            </ProductCard>
          );
        })}
      </InnerContainer>
    </Container>
  );
}

BlockBuilderProductsComparisonGrid.propTypes = {
  value: PropTypes.shape({
    products: PropTypes.arrayOf(
      PropTypes.shape({
        _key: PropTypes.string,
        topHeader: PropTypes.string,
        useProductImage: PropTypes.bool,
        product: PropTypes.shape({
          store: PropTypes.shape({
            previewImageUrl: PropTypes.string,
            title: PropTypes.string,
            vendor: PropTypes.string,
            productType: PropTypes.string,
            tags: PropTypes.string,
            variants: PropTypes.arrayOf(
              PropTypes.shape({
                store: PropTypes.shape({
                  price: PropTypes.number,
                  compareAtPrice: PropTypes.number,
                  gid: PropTypes.string,
                }),
              })
            ),
          }),
        }),
        media: PropTypes.shape({
          asset: PropTypes.shape({}),
          hotspot: PropTypes.shape({}),
          crop: PropTypes.shape({}),
          alt: PropTypes.string,
        }),
        overwriteProductCardTitle: PropTypes.bool,
        productCardTitle: PropTypes.string,
        enableHighlightSection: PropTypes.bool,
        leftHighlightSection: PropTypes.string,
        rightHighlightSection: PropTypes.string,
        bottomHighlightSection: PropTypes.string,
        promoSection: PropTypes.string,
        benefitsSection: PropTypes.shape({}),
        cta: PropTypes.shape({
          caption: PropTypes.string,
          linkType: PropTypes.string,
          internalLink: PropTypes.shape({}),
          href: PropTypes.string,
          blank: PropTypes.bool,
        }),
      })
    ),
  }).isRequired,
  getInternalLink: PropTypes.func.isRequired,
};

export default BlockBuilderProductsComparisonGrid;
